import apiClient, { baseURL } from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

const FEE_TYPE_ENDPOINT = {
  UPDATE_FEE_INVOICE_ITEM: "/invoice/update-invoice-item",
  PAY_ADMISSION_FEE: "/invoice/pay-admission-fee",
  GET_FEE_INVOICE: "/invoice",
  PAY_OTHER_FEE: "/invoice/pay-monthly-fee",
  CREATE_MONTHLY_FEE: "/invoice/create-monthly-fee",
  CREATE_TRANSPORT_INVOICE: "/transport/invoices",
  GET_INVOICE_SUMMARY: "/invoice/summary",
  GET_TRANSPORT_INVOICES: "/transport/invoices",
  GET_TRANSPORT_SUMMARY: "/transport/invoices/summary",
  SPLIT_INVOICE_INTO_INSTALLMENTS: "/invoice",
  FEE_RECEIPT: "/receipt",
}

export type ReceiptPrinter = "normal" | "thermal"

// Direct-navigation URL (window.open), not an apiClient call — the backend
// streams a PDF (normal, A4) or an auto-printing HTML page (thermal, 76mm
// TM-U220 roll) rather than JSON. Auth rides along on the httpOnly cookie
// the same as any other top-level navigation to the API host.
export const getFeeReceiptUrl = (invoiceId: string, printer: ReceiptPrinter) =>
  `${baseURL}${FEE_TYPE_ENDPOINT.FEE_RECEIPT}/${invoiceId}?printer=${printer}`

export type InvoiceItem = {
  feeGroupId: string
  // Explicit FeeType _ids to bill for this month — restricts a fee group
  // that holds more than one FeeType (e.g. Tuition Fee + STEAM Education
  // Fee both under "Tuition") to only the ones actually opted into. Omitted
  // entirely by the "other fees" (one-off) entries, which use feeTypeId.
  feeTypeIds?: string[]
  feeTypeId?: string
  month: string
  status: string
  fine?: number
  discount?: {
    profile: number
    manual: number
  }
}

export type LibraryFineItem = {
  issuedBookLogId: string
  type: "damage" | "overdue" | "misplaced"
  amount: number
  /** Manual discount (profile discount is tuition-only). */
  manualDiscount?: number
}

export type MonthlyFeeRequest = {
  enrollmentId: string
  invoices: InvoiceItem[]
  libraryFines?: LibraryFineItem[]
}

export type CreateMonthlyFeeResponse = {
  success: boolean
  message: string
  data: any[]
}

export type FeeInvoiceResponse = {
  success: boolean
  data: any[]
  pagination: {
    total: number
    totalPages: number
    currentPage: number
    limit: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}
export interface FeeBreakdownItem {
  title: string
  amount: string // kept as string because backend returns it as string
}

export interface MonthBreakdownItem {
  month: string // e.g. "Nov-25"
  amount: number
}

export type InvoiceSummaryResponse = {
  totalAmount?: number
  totalCollected: number
  totalDiscount: number
  totalFine: number
  totalOnline: number
  totalCash: number
  feeBreakdown: FeeBreakdownItem[]
  monthBreakdownData: MonthBreakdownItem[]
}

const updateFeeInvoiceItem = async (
  invoiceId: string,
  invoiceItemId: string,
  data: {
    dueDate?: string
    discount?: number | { manual: number; profile: number }
    paymentType?: string
    notes?: string
    amount?: number
  }
) => {
  const response = await apiClient({
    url: `${FEE_TYPE_ENDPOINT.UPDATE_FEE_INVOICE_ITEM}/${invoiceId}/${invoiceItemId}`,
    method: "PUT",
    data,
  })
  return response.data
}

const payAdmissionFee = async (id: string) => {
  const response = await apiClient({
    url: `${FEE_TYPE_ENDPOINT.PAY_ADMISSION_FEE}/${id}`,
    method: "PUT",
  })
  return response.data
}

const payOtherFee = async (id: string) => {
  const response = await apiClient({
    url: `${FEE_TYPE_ENDPOINT.PAY_OTHER_FEE}/${id}`,
    method: "PUT",
  })
  return response.data
}

const getAllFeesInvoices = async ({
  page = 1,
  limit = 10,
  searchText: search,
  status,
  section,
  startDate,
  endDate,
  sortBy,
  sortOrder,
  feeGroup,
  system,
  session,
  class: classId,
  branch,
  enrollmentId,
}: {
  page: number
  limit?: number
  searchText?: string
  status?: string
  section?: string
  startDate?: string
  endDate?: string
  sortBy?: string
  sortOrder?: string
  feeGroup?: string
  system?: string
  session?: string
  class?: string
  branch?: string
  enrollmentId?: string
}) => {
  const response = await apiClient({
    url: appendQueryParams(FEE_TYPE_ENDPOINT.GET_FEE_INVOICE, {
      page,
      limit,
      searchText: search,
      class: classId,
      status,
      section,
      startDate,
      endDate,
      sortBy,
      sortOrder,
      feeGroup,
      system,
      session,
      branch,
      enrollmentId,
    }),
    method: "GET",
  })
  return response.data
}

const getAllInvoicesForPrint = async ({
  searchText: search,
  status,
  section,
  startDate,
  endDate,
  feeGroup,
  system,
  session,
  class: classId,
  branch,
  enrollmentId,
  collectedBy,
  fields,
}: {
  searchText?: string
  status?: string
  section?: string
  startDate?: string
  endDate?: string
  feeGroup?: string
  system?: string
  session?: string
  class?: string
  branch?: string
  enrollmentId?: string
  // Admin-only: scope to one cashier's collections (Cashier Report screen).
  // A cashier's own view is already locked to themselves server-side
  // regardless of this param.
  collectedBy?: string
  // "summary": skips every join the backend normally does for student/
  // class/admission/session/branch/createdBy -- only safe for a caller
  // that reads invoiceItems-level numbers (amount/discount/fine/month/fee
  // group+type) and nothing about who the invoice belongs to, and that
  // never also passes searchText or section (unsupported together with
  // this flag -- see routes/invoice.js). Built for the Tuition Fee
  // Breakdown card, which used to pull the full shape just to sum a few
  // fields -- ~1900 Naseerabad invoices with every join took 6+ seconds.
  fields?: "summary"
}) => {
  const response = await apiClient({
    url: appendQueryParams("/invoice/all-invoices", {
      searchText: search,
      class: classId,
      status,
      section,
      startDate,
      endDate,
      feeGroup,
      system,
      session,
      branch,
      enrollmentId,
      collectedBy,
      fields,
    }),
    method: "GET",
  })
  return response.data
}

export type CashierListItem = {
  _id: string
  firstName: string
  lastName: string
  email: string
}

// For the Cashier Report's "collected by" filter -- a plain GET /auth/users
// (role=cashier) needs admin-only user.view/role.view permission, so a
// cashier's own call to it 403s silently and the dropdown stays empty.
// This hits a dedicated endpoint gated by the same fees permission the
// report itself already needs, scoped to the caller's own branch.
const getCashiersList = async (): Promise<{ success: boolean; data: CashierListItem[] }> => {
  const response = await apiClient({ url: "/invoice/cashiers", method: "GET" })
  return response.data
}

const exportInvoicesToExcel = async (params: {
  searchText?: string
  status?: string
  section?: string
  startDate?: string
  endDate?: string
  feeGroup?: string
  system?: string
  session?: string
  class?: string
  branch?: string
  enrollmentId?: string
}) => {
  const response = await apiClient({
    url: appendQueryParams("/invoice/all-invoices", params),
    method: "GET",
  })
  return response.data
}

const createMonthlyFee = async (
  data: MonthlyFeeRequest
): Promise<CreateMonthlyFeeResponse> => {
  const response = await apiClient({
    url: FEE_TYPE_ENDPOINT.CREATE_MONTHLY_FEE,
    method: "POST",
    data,
  })
  return response.data
}

export type TransportInvoiceItem = {
  month: string
  discount?: { profile?: number; manual?: number }
  fine?: number
  status?: string
}

export type CreateTransportInvoiceRequest = {
  studentId: string
  transportInvoices: TransportInvoiceItem[]
}

const createTransportInvoice = async (
  data: CreateTransportInvoiceRequest
): Promise<CreateMonthlyFeeResponse> => {
  const response = await apiClient({
    url: FEE_TYPE_ENDPOINT.CREATE_TRANSPORT_INVOICE,
    method: "POST",
    data,
  })
  return response.data
}

const getInvoiceSummary = async (params: {
  sessionId?: string
  class?: string
  section?: string
  studentId?: string
  enrollmentId?: string
  month?: string
  status?: string
  dateFrom?: string
  dateTo?: string
  branch?: string
  system?: string
  collectedBy?: string
}): Promise<InvoiceSummaryResponse> => {
  const response = await apiClient({
    url: appendQueryParams(FEE_TYPE_ENDPOINT.GET_INVOICE_SUMMARY, params),
    method: "GET",
  })
  return response.data
}

export type InstallmentPlanItem = {
  amount: number
  dueDate: string
}

const splitInvoiceIntoInstallments = async (
  invoiceId: string,
  invoiceItemId: string,
  installments: InstallmentPlanItem[]
) => {
  const response = await apiClient({
    url: `${FEE_TYPE_ENDPOINT.SPLIT_INVOICE_INTO_INSTALLMENTS}/${invoiceId}/items/${invoiceItemId}/installments`,
    method: "POST",
    data: { installments },
  })
  return response.data
}

const cancelInvoice = async (invoiceId: string) => {
  const response = await apiClient({
    url: `/invoice/${invoiceId}`,
    method: "DELETE",
  })
  return response.data
}

export type TransportInvoiceFilters = {
  page?: number
  limit?: number
  branch?: string
  studentId?: string
  sessionId?: string
  month?: string
  status?: string
  startDate?: string
  endDate?: string
  system?: string
  class?: string
  section?: string
  contractorId?: string
  sortBy?: string
  sortOrder?: string
}

export type TransportInvoiceResponse = {
  success: boolean
  data: any[]
  pagination: {
    total: number
    totalPages: number
    currentPage: number
    limit: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

const getTransportInvoices = async (
  params: TransportInvoiceFilters = {}
): Promise<TransportInvoiceResponse> => {
  const {
    page = 1,
    limit = 10,
    branch,
    studentId,
    sessionId,
    month,
    status,
    startDate,
    endDate,
    system,
    class: classId,
    section,
    contractorId,
    sortBy = "createdAt",
    sortOrder = "desc",
  } = params
  const response = await apiClient({
    url: appendQueryParams(FEE_TYPE_ENDPOINT.GET_TRANSPORT_INVOICES, {
      page,
      limit,
      branch,
      studentId,
      sessionId,
      month,
      status,
      startDate,
      endDate,
      system,
      class: classId,
      section,
      contractorId,
      sortBy,
      sortOrder,
    }),
    method: "GET",
  })
  return response.data
}

const getTransportInvoiceSummary = async (params: {
  sessionId?: string
  class?: string
  section?: string
  studentId?: string
  month?: string
  status?: string
  dateFrom?: string
  dateTo?: string
  branch?: string
  system?: string
  contractorId?: string
}): Promise<InvoiceSummaryResponse> => {
  const response = await apiClient({
    url: appendQueryParams(FEE_TYPE_ENDPOINT.GET_TRANSPORT_SUMMARY, {
      sessionId: params.sessionId,
      class: params.class,
      section: params.section,
      studentId: params.studentId,
      month: params.month,
      status: params.status,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      branch: params.branch,
      system: params.system,
      contractorId: params.contractorId,
    } as Record<string, string | undefined>),
    method: "GET",
  })
  return response.data
}

const cancelTransportInvoice = async (invoiceId: string) => {
  const response = await apiClient({
    url: `${FEE_TYPE_ENDPOINT.GET_TRANSPORT_INVOICES}/${invoiceId}`,
    method: "DELETE",
  })
  return response.data
}

const getTransportInvoicesForPrint = async (params: Omit<TransportInvoiceFilters, "page" | "limit">) => {
  const response = await apiClient({
    url: appendQueryParams(FEE_TYPE_ENDPOINT.GET_TRANSPORT_INVOICES, {
      ...params,
      page: 1,
      limit: 1000,
    }),
    method: "GET",
  })
  return response.data
}

export {
  updateFeeInvoiceItem,
  payAdmissionFee,
  getAllFeesInvoices,
  getAllInvoicesForPrint,
  getCashiersList,
  exportInvoicesToExcel,
  payOtherFee,
  createMonthlyFee,
  createTransportInvoice,
  getInvoiceSummary,
  splitInvoiceIntoInstallments,
  cancelInvoice,
  getTransportInvoices,
  getTransportInvoiceSummary,
  cancelTransportInvoice,
  getTransportInvoicesForPrint,
}
